import { memo } from "react"

import { useGetScanHistoryStatistics } from "@/api/useScanHistory"
import { Column, Flex } from "@/atoms"
import {
  getDateInFormat,
  getUTCDate,
  MONTH_DAY_FORMAT,
  MONTH_DAY_YEAR_FORMAT
} from "@/helpers/date.helpers"
import { Heading } from "@/molecules/Heading"
import { getCountsOverTimeScanHistoryStatisticsSelector } from "@/pages/GovernancePage/applications/scan-history/useScanHistory.selector"
import { Legend } from "./Legend"
import { getStatusTranslationKey } from "./options"

import styles from "../HeaderScanHistoryTab.module.scss"

import type {
  Results,
  ScansStackedColumnProps
} from "./ScansStackedColumn.types"

export const ScansStackedColumn: React.FC<ScansStackedColumnProps> = memo(
  function ScansStackedColumnComponent(props) {
    const { colorsByType, t } = props
    const { response } = useGetScanHistoryStatistics(undefined, {
      select: getCountsOverTimeScanHistoryStatisticsSelector
    })
    const config = {
      height: 125,
      interactions: [{ type: "active-region", enable: false }],
      responsive: true,
      legend: false as const,
      isStack: true,
      dodgePadding: 0.5,
      intervalPadding: 1,
      widthRatio: 1,
      xAxis: {
        tickInterval: 7,
        label: {
          formatter: (text: string) =>
            getDateInFormat(getUTCDate(Number(text)), MONTH_DAY_FORMAT)
        }
      },
      yAxis: {
        grid: {
          line: {
            style: {
              stroke: "#f0f0f0"
            }
          }
        }
      },
      color: (data: Record<string, unknown>) => {
        const type = (data as { type?: Results })?.type

        if (type) return colorsByType[type]

        return "#adc6ff"
      }
    }
    const { metadata, data = [] } = response || {}
    const lastValueByTimestamp = metadata?.lastValueByTimestamp || {}

    return (
      <Column
        seriesField="type"
        xField="timestamp"
        yField="value"
        tooltip={{
          domStyles: {
            "g2-tooltip": { zIndex: "20" }
          },
          customContent: (timestamp, data) => {
            return (
              <Flex vertical gap={8} className={styles.containerTooltipColumn}>
                <Heading
                  level={6}
                  title={getDateInFormat(
                    getUTCDate(Number(timestamp)),
                    MONTH_DAY_YEAR_FORMAT
                  )}
                />
                <span className={styles.textTooltipColumn}>
                  {t("totalScans")}
                </span>
                {data.reverse().map(({ color, value, name: status }, index) => (
                  <Flex key={index} flex={1} gap={4}>
                    <Legend
                      items={[
                        {
                          color,
                          label: (
                            <Flex
                              flex={1}
                              justify="space-between"
                              align="center"
                            >
                              <Flex flex={1}>
                                {t(getStatusTranslationKey(status as string))}
                              </Flex>
                              <Flex>{value}</Flex>
                            </Flex>
                          )
                        }
                      ]}
                      classes={{
                        container: styles.itemTooltipColumn,
                        text: styles.textTooltipColumn,
                        item: styles.itemTooltipColumn
                      }}
                    />
                  </Flex>
                ))}
              </Flex>
            )
          }
        }}
        columnStyle={({ timestamp, type }) => ({
          radius: lastValueByTimestamp[timestamp] === type ? [4, 4, 0, 0] : 0
        })}
        data={data}
        {...config}
      />
    )
  },
  () => true // Prevent re-renders
)
